import { inject } from 'vue';
import { useStore } from 'vuex';
import type { Emitter } from 'mitt';
import PermissionService from '@/utils/PermissionService';
import { Roles } from '@/models/Roles';
import { ACCESS_LEVEL } from '@/models/user/ACCESS_LEVEL';
import { isEditingText } from '@/modules/map-editor';
import type { Node } from '@/modules/map-editor';

//: Nudge step in map units; Shift uses the larger step (mirrors Figma/Miro).
const NUDGE_STEP = 1;
const NUDGE_STEP_LARGE = 10;

/**
 * Canvas-level keyboard shortcuts for editing the map.
 * Reads selection from the store and dispatches the same actions the
 * toolbar buttons use, so behaviour (and collaboration sync) stays identical.
 */
export function useKeyboardShortcuts() {
  const store = useStore();
  const emitter = inject<Emitter<Record<string, unknown>>>('emitter');

  function canEdit(): boolean {
    if (store.getters['user/getUserAccessLevel'] === ACCESS_LEVEL.READ) return false;
    return PermissionService.permissionCheck({
      userRole: store.getters['user/getCurrentUserRole'],
      permission: Roles.Student
    });
  }

  function selectedNodeList(): Node[] {
    const selectedMap = store.getters['nodes/getSelectedNodes'] as Map<number, Node> | undefined;
    return selectedMap ? Array.from(selectedMap.values()) : [];
  }

  async function deleteSelected(nodes: Node[]): Promise<void> {
    for (const node of nodes) {
      await store.dispatch('nodes/removeNodeRequest', node.id);
      await store.dispatch('nodes/removeNode', node.id);
      await store.dispatch('nodes/content/checkNavigationForDeletedNode', node.id);
    }
    store.dispatch('tocWarnings/checkDeletedNodes');
  }

  function nudgeSelected(nodes: Node[], dx: number, dy: number): void {
    store.dispatch('nodes/position/nodeMoveBy', {
      nodeIds: nodes.map(node => node.id),
      dx,
      dy
    });
  }

  function handleShortcutKeyDown(event: KeyboardEvent): void {
    // Never steal keystrokes from inputs (incl. the node title field).
    if (isEditingText()) return;
    if (!canEdit()) return;

    const mod = event.ctrlKey || event.metaKey;
    const nodes = selectedNodeList();

    // Select all — works without an existing selection.
    if (mod && (event.key === 'a' || event.key === 'A')) {
      event.preventDefault();
      store.dispatch('nodes/selectMultipleNodes', store.getters['nodes/getNodes']);
      return;
    }

    if (!nodes.length) return;

    // Delete / Backspace — remove the selected node(s).
    if (event.key === 'Delete' || event.key === 'Backspace') {
      event.preventDefault();
      deleteSelected(nodes);
      return;
    }

    // Escape — clear the selection.
    if (event.key === 'Escape') {
      event.preventDefault();
      store.dispatch('nodes/deselectAllNodes');
      return;
    }

    // Ctrl/Cmd+D — duplicate. Clone only supports a single active node.
    if (mod && (event.key === 'd' || event.key === 'D')) {
      event.preventDefault();
      if (nodes.length === 1) store.dispatch('nodes/cloneNodeRequest', nodes[0].id);
      return;
    }

    // Enter / F2 — focus the title of the (single) selected node for editing.
    if ((event.key === 'Enter' || event.key === 'F2') && nodes.length === 1) {
      event.preventDefault();
      emitter?.emit('focusFormattingField');
      return;
    }

    // Arrow keys — nudge; Shift for the larger step.
    const step = event.shiftKey ? NUDGE_STEP_LARGE : NUDGE_STEP;
    switch (event.key) {
      case 'ArrowLeft':
        event.preventDefault();
        nudgeSelected(nodes, -step, 0);
        return;
      case 'ArrowRight':
        event.preventDefault();
        nudgeSelected(nodes, step, 0);
        return;
      case 'ArrowUp':
        event.preventDefault();
        nudgeSelected(nodes, 0, -step);
        return;
      case 'ArrowDown':
        event.preventDefault();
        nudgeSelected(nodes, 0, step);
        return;
      default:
        break;
    }
  }

  return { handleShortcutKeyDown };
}
